import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { Autocomplete, TextField, Divider, Switch } from '@mui/material';

import { type User, type Permission, UserPermissions, UserRoles } from '@models/user';
import type { CommonData } from '@models/commonData';
import { isUserAdmin, userHasPermission } from '@utils/access';

import { useToast } from '@hooks/useToast';
import { useMeStore } from '@features/users/store';
import { useUserQuery } from '@features/users/queries';
import { useCommonDataQuery } from '@features/commonData/queries';
import { useUpdateUser } from '@features/users/mutations';

import LoadingAnimation from '@components/LoadingAnimation';
import PrimaryButton from '@components/PrimaryButton';
import SecondaryButton from '@components/SecondaryButton';
import s from './UserEditPage.module.css';

const filterPermissions = (permissions: Permission[]) => permissions.filter((p) => p.title);
const createRolesList = (roles: string[], isMeAdmin: boolean) => {
  const rolestList = roles.map((r) => ({ title: r }));
  const adminIndex = rolestList.findIndex((r) => r.title === UserRoles.ADMIN);

  if (!isMeAdmin) {
    rolestList.splice(adminIndex, 1);
  }

  return rolestList;
};

type EditFormProps = {
  currentUser: User;
  commonData: CommonData;
  isDisabled: boolean;
  setCurrentUser: React.Dispatch<React.SetStateAction<User | null>>;
};

const EditForm = ({ currentUser, commonData, isDisabled, setCurrentUser }: EditFormProps) => {
  // Roles
  const meStore = useMeStore();
  const isMeAdmin = isUserAdmin(meStore as User);
  const rolesList = createRolesList(commonData?.roles || [], isMeAdmin);
  const selectedRoles = useMemo(() => {
    if (!currentUser.roles || currentUser.roles.length < 1) return [];

    return rolesList.filter((r) => currentUser.roles.includes(r.title));
  }, [currentUser, rolesList]);

  // Permissions
  const permissionList = filterPermissions(commonData?.permissions || []);
  const selectedPermissions = useMemo(() => {
    if (!currentUser.permissions || currentUser.permissions.length < 1) return [];

    return permissionList.filter((p) =>
      currentUser.permissions.some((cp) => cp.code === p.code && cp.title === p.title),
    );
  }, [currentUser, permissionList]);

  return (
    <>
      <TextField
        size="medium"
        value={currentUser?.email || ''}
        name="email"
        label="Email"
        disabled={isDisabled}
        required
      />
      <Autocomplete
        id="tags-outlined"
        multiple
        options={rolesList}
        getOptionLabel={(option) => option.title}
        value={selectedRoles}
        disabled={isDisabled}
        renderInput={(params) => (
          <TextField
            {...params}
            variant="outlined"
            label="Roles"
            disabled={isDisabled}
            placeholder="Roles..."
          />
        )}
        onChange={(_, newValue) => {
          setCurrentUser((prev) =>
            prev ? { ...prev, roles: (newValue as { title: string }[]).map((r) => r.title) } : prev,
          );
        }}
      />
      <Autocomplete
        multiple
        options={permissionList}
        getOptionLabel={(option) => option.title}
        value={selectedPermissions}
        disabled={isDisabled}
        renderInput={(params) => (
          <TextField
            {...params}
            variant="outlined"
            label="Permissions"
            disabled={isDisabled}
            placeholder="Permissions..."
          />
        )}
        onChange={(_, newValue) => {
          setCurrentUser((prev) =>
            prev ? { ...prev, permissions: newValue as Permission[] } : prev,
          );
        }}
      />
    </>
  );
};

function UserEditPage() {
  const navigate = useNavigate();
  const toast = useToast();
  const location = useLocation();
  const userId = location.pathname.split('/')[3];
  const { data: user, isLoading: isLoadingUser } = useUserQuery(userId);
  const { data: commonData, isLoading: isLoadingCommonData } = useCommonDataQuery();
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const meStore = useMeStore();
  const [isRestrictedEdit, setIsRestrictedEdit] = useState(true);
  const { mutate } = useUpdateUser();

  useEffect(() => {
    if (isLoadingUser || isLoadingCommonData) return;
    if (currentUser) return;

    if (user) {
      setCurrentUser(user);
      const isEditAvailable = userHasPermission(meStore as User, UserPermissions.EDIT_USERS);
      setIsRestrictedEdit(!isEditAvailable);
    } else {
      toast.error('User has not been found');
      navigate('/dashboard/users', { replace: true });
    }
  }, [isLoadingUser, isLoadingCommonData, user, currentUser, meStore, toast, navigate]);

  if (isLoadingUser || isLoadingCommonData) return <LoadingAnimation />;
  if (!currentUser || !commonData) return <div>No user data</div>;

  const handleCancel = () => {
    navigate('/dashboard/users', { replace: true });
  };

  const handleUpdate = async () => {
    if (currentUser.roles.length < 1) {
      toast.error('User must have at least one role');
      return;
    }
    if (currentUser.permissions.length < 1) {
      toast.error('User must have at least one permission');
      return;
    }
    const sanitizedPermissions = currentUser.permissions.filter((p) => p.title); // Remove "*" permission, as BE won't allow to save it then
    const sanitizedUser = { ...currentUser, permissions: sanitizedPermissions };

    mutate({ id: sanitizedUser.id, user: sanitizedUser });
  };

  const handleUserActiveChange = () => {
    setCurrentUser((prev) => (prev ? { ...prev, isActive: !prev.isActive } : prev));
  };

  return (
    <div className={s.usersEditPage}>
      <div className={s.usersHeaderBar}>
        <h1 className={s.usersEditTitle}>Edit user</h1>
        <div className={s.usersHeaderAction}>
          <Switch
            checked={currentUser.isActive}
            onChange={handleUserActiveChange}
            disabled={isRestrictedEdit}
          />
        </div>
      </div>
      <div className={s.userEditForm}>
        <div className={s.usersInputs}>
          <EditForm
            currentUser={currentUser as User}
            commonData={commonData as CommonData}
            isDisabled={isRestrictedEdit}
            setCurrentUser={setCurrentUser}
          />
        </div>
        <Divider />
        <div className={s.usersActions}>
          <PrimaryButton
            title={'Update user'}
            isDisabled={isRestrictedEdit}
            onClick={handleUpdate}
          />
          <SecondaryButton title={'Cancel'} onClick={handleCancel} />
        </div>
      </div>
    </div>
  );
}

export default UserEditPage;
